import React, { useEffect, useState, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { ListGroup, FormGroup, InputGroup,
    Alert, Button, Image, FormControl, Badge, Dropdown, Form, Modal } from 'react-bootstrap'
import FontAwesomeIcon from '@severed-links/common.font-awesome-icon'
import * as s from './RecapComments.scss'
import { getRecapComments, addRecapComment, deleteRecapComment } from '@severed-links/common.redherrings-reducers/cinderella'
import moment from 'moment-timezone'
import pluralize from 'pluralize'
import { playerUrl } from '@severed-links/common.redherrings-constants'
import Confirm from '@severed-links/common.confirm'
import { blockUser, getBlockedUsers, getMyReportedItems, getReportedItems, reportItem, unblockUser, withdrawReportedItem } from '@severed-links/common.redherrings-reducers/reporting'
import { createNotification } from '@severed-links/common.redherrings-reducers/notifications'

const REPORT_REASONS = [
    'Objectionable content',
    'Harassment or bullying',
    'Hate speech',
    'Spam',
    'Other',
]

const actionErrorMessage = (action, fallback = 'The request could not be completed.') =>
    action.payload?.response?.message || action.payload?.message || fallback

const NewRecapComment = ({ newComment = '', avatarUrl = null, onChange, onPost, onCancelEdit, editMode = false, inputRef = null }) => (
    <div className={s.newCommentContainer}>
        <FormGroup className={s.formGroup}>
            <InputGroup className={s.inputGroup}>                    
                <InputGroup.Text className={s.newCommentLabel}>Comment</InputGroup.Text>
                <InputGroup.Text className={s.newCommentAvatar}><Image src={playerUrl(avatarUrl)} /></InputGroup.Text>
                <FormControl ref={inputRef} value={newComment} multiple
                    className={s.newCommentTextBox}
                    onKeyUp={e => e.key === 'Enter' && newComment ? onPost() : null}
                    onChange={e => onChange(e)} />
                {editMode ?
                <Button className={s.cancelButton} onClick={() => onCancelEdit()}>
                    <FontAwesomeIcon name='times' />
                </Button>
                : null}
                <Button onClick={() => onPost()} disabled={!newComment}>
                    <FontAwesomeIcon name='comment' />
                    <span className={s.newCommentButtonLabel}>post</span>
                </Button>
            </InputGroup>
        </FormGroup>
    </div>
)

const RecapComment = ({ currentTime = null, _id, playerId, comment = '', firstName, lastName, avatarUrl, createdAt, cinderellas = 0, onDelete, canDelete = false, canEdit = false, canModerate = false, canUndoReport = false, onEdit, isEditing = false, isEdited = false, isAdmin = false, isBlocked = false, reportedItem = null, onReport, onBlock, onUnblock, onUndoReport, isSavingBlock = false, isSavingReport = false }) => (
    <ListGroup.Item className={`${s.commentItem}${isEditing ? ` ${s.isEditing}` : ''}${isBlocked ? ` ${s.blockedCommentItem}` : ''}`}>
        <div className={s.heading}>
            <div className={s.avatar}>
                <Image src={playerUrl(avatarUrl)} />
            </div>
            <div className={s.nameContainer}>
                <div className={s.name}>{firstName} {lastName}{isAdmin ? <Badge className={s.adminBadge} bg={'light'} text={'dark'}>Admin</Badge> : null}</div>
                <div className={s.cinderellas}>{pluralize('Cinderella', cinderellas, true)}</div>
            </div>
            <RecapUpdateTime createdAt={createdAt} currentTime={currentTime} />
        </div>
        {isBlocked ?
        <div className={s.blockedComment}>
            <span><FontAwesomeIcon name='ban' /> Comment hidden — user blocked</span>
            <div className={s.blockedActions}>
                {reportedItem && canUndoReport ?
                <Button variant='outline-secondary' size='sm' disabled={isSavingReport}
                    onClick={() => onUndoReport(reportedItem._id)}>
                    <FontAwesomeIcon name='flag' /> Undo report
                </Button>
                : null}
                <Button variant='outline-secondary' size='sm' disabled={isSavingBlock}
                    onClick={() => onUnblock(playerId)}>
                    <FontAwesomeIcon name='undo' /> Unblock
                </Button>
            </div>
        </div>
        : <div className={s.comment}>{comment}{isEdited ? <span className={s.isEdited}>(edited)</span> : null}</div>}
        {!isBlocked && reportedItem ?
        <div className={s.reportedStatus}>
            <span><FontAwesomeIcon name='flag' /> Reported — awaiting review</span>
            {canUndoReport ? <Button variant='link' size='sm' disabled={isSavingReport}
                onClick={() => onUndoReport(reportedItem._id)}>Undo report</Button>
            : null}
        </div>
        : null}
        {!isBlocked && (canEdit || canDelete || canModerate) ?
        <Dropdown className={s.actionMenu} align='end'>
            <Dropdown.Toggle variant='link' className={s.actionMenuToggle}
                aria-label='Comment actions' title='Comment actions'>
                <FontAwesomeIcon name='ellipsis-v' />
            </Dropdown.Toggle>
            <Dropdown.Menu>
                {canEdit ?
                <Dropdown.Item onClick={() => onEdit(_id)}>
                    <FontAwesomeIcon name='pencil-alt' />
                    <span>Edit comment</span>
                </Dropdown.Item>
                : null}
                {canDelete ?
                <Confirm variant='danger'
                    onConfirm={() => onDelete(_id)}
                    heading='Delete Comment'
                    confirmText='delete comment'
                    body='Are you sure you want to delete this comment?'
                    buttonIcon='trash' buttonText='Delete comment'
                    buttonClassName={s.deleteMenuItem} />
                : null}
                {canModerate ?
                <>
                    {canEdit || canDelete ? <Dropdown.Divider /> : null}
                    <Dropdown.Item onClick={() => onReport(_id)}>
                        <FontAwesomeIcon name='flag' />
                        <span>Report comment</span>
                    </Dropdown.Item>
                    <Dropdown.Item onClick={() => onBlock(_id)} className={s.blockMenuItem}>
                        <FontAwesomeIcon name='ban' />
                        <span>Block user</span>
                    </Dropdown.Item>
                </>
                : null}
            </Dropdown.Menu>
        </Dropdown>
        : null}
    </ListGroup.Item>
)

const ModerationModal = ({ action, comment, isSaving, error, onClose, onSubmit }) => {
    const [reason, setReason] = useState(REPORT_REASONS[0])
    const [details, setDetails] = useState('')

    useEffect(() => {
        setReason(REPORT_REASONS[0])
        setDetails('')
    }, [action, comment?._id])

    if (!action || !comment) return null
    const isBlock = action === 'block'
    const playerName = `${comment.firstName || ''} ${comment.lastName || ''}`.trim() || 'this user'

    return (
        <Modal show onHide={onClose} centered>
            <Modal.Header closeButton>
                <Modal.Title>{isBlock ? `Block ${playerName}?` : 'Report comment'}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <p>{isBlock ?
                    'Their recap comments will be hidden from you immediately. This comment will also be reported to an administrator.'
                    : 'An administrator will review this comment within 24 hours.'}</p>
                {error ? <Alert variant='danger'>{error}</Alert> : null}
                <Form.Group className='mb-3'>
                    <Form.Label>Reason</Form.Label>
                    <Form.Select value={reason} onChange={e => setReason(e.target.value)}>
                        {REPORT_REASONS.map(item => <option value={item} key={item}>{item}</option>)}
                    </Form.Select>
                </Form.Group>
                <Form.Group>
                    <Form.Label>Additional details <small>(optional)</small></Form.Label>
                    <Form.Control as='textarea' rows={3} value={details}
                        onChange={e => setDetails(e.target.value)} />
                </Form.Group>
            </Modal.Body>
            <Modal.Footer>
                <Button variant='light' onClick={onClose} disabled={isSaving}>Cancel</Button>
                <Button variant={isBlock ? 'warning' : 'primary'} disabled={isSaving}
                    onClick={() => onSubmit(reason, details)}>
                    <FontAwesomeIcon name={isBlock ? 'ban' : 'flag'} />
                    {' '}{isSaving ? 'Submitting...' : (isBlock ? 'Block and report' : 'Report comment')}
                </Button>
            </Modal.Footer>
        </Modal>
    )
}

const RecapUpdateTime = ({ currentTime = null, createdAt = null }) => (
    <div className={s.createdAt}>{moment(createdAt).fromNow()}</div>
)

const RecapComments = ({ recapId = null }) => {

    const dispatch = useDispatch()
    const { _id: playerId, avatarUrl, roles } = useSelector(state => state.account)
    const isCinderellaAdmin = (roles || []).includes('CinderellaAdmin')
    const recapComments = useSelector(state => state.cinderella.recapComments)[`${recapId}`] || {}
    const comments = recapComments.comments || []
    const { blockedPlayerIds = [], myReportedItems = [], reportedItems = [], isSavingBlock, isSavingReport } = useSelector(state => state.reporting)
    const [newComment, setNewComment] = useState('')
    const [editCommentId, setEditCommentId] = useState(null)
    const [currentTime, setCurrentTime] = useState(moment().toISOString())
    const [moderationAction, setModerationAction] = useState(null)
    const [moderationCommentId, setModerationCommentId] = useState(null)
    const [moderationError, setModerationError] = useState(null)
    const _bottomOfComments = useRef(null)
    const _commentInput = useRef(null)

    useEffect(() => {
        let timerId = setInterval(() => setCurrentTime(moment().toISOString()), 5000)
        return () => clearInterval(timerId)
    }, [])

    useEffect(() => { 
        if (recapId) { 
            dispatch(getRecapComments(recapId))
        }
    }, [recapId])

    useEffect(() => {
        dispatch(getBlockedUsers('Cinderella', 'RecapComments'))
        dispatch(getMyReportedItems('Cinderella', 'RecapComment'))
        if (isCinderellaAdmin) dispatch(getReportedItems('Cinderella', 'Open'))
    }, [isCinderellaAdmin])

    const scrollToBottom = () => {
        _bottomOfComments.current?.scrollIntoView({ behavior: 'smooth' })
    }

    const handleCommentPost = () => {
        if (newComment) {
            dispatch(addRecapComment(recapId, playerId, newComment, editCommentId))
            .then(action => {
                if (action.payload?.error || action.error) {
                    dispatch(createNotification({
                        headline: editCommentId ? 'Edit Comment' : 'Post Comment',
                        message: actionErrorMessage(action, 'The comment could not be saved. Please revise it and try again.'),
                        type: 'error',
                        timeout: 6000,
                    }))
                    _commentInput.current?.focus()
                    _commentInput.current?.select()
                    return
                }
                setNewComment('')
                setEditCommentId(null)
                scrollToBottom()
            })
        }
    }

    const handleDeleteComment = recapCommentId => {
        dispatch(deleteRecapComment(recapCommentId))
        .then(() => scrollToBottom())
    }

    const handleCancelEdit = () => {
        setNewComment('')
        setEditCommentId(null)
    }

    const handleEdit = _editCommentId => {
        setNewComment((comments.find(i => i._id === _editCommentId) || {}).comment || '')
        setEditCommentId(_editCommentId)
    }

    const openModeration = (action, commentId) => {
        setModerationAction(action)
        setModerationCommentId(commentId)
        setModerationError(null)
    }

    const closeModeration = () => {
        setModerationAction(null)
        setModerationCommentId(null)
        setModerationError(null)
    }

    const handleModeration = (reason, details) => {
        const item = {
            app: 'Cinderella',
            itemType: 'RecapComment',
            itemId: moderationCommentId,
            reason,
            details,
        }
        const action = moderationAction === 'block' ? blockUser(item) : reportItem(item)
        dispatch(action).then(result => {
            if (result.error || result.payload?.error || result.payload?.response?.error) {
                setModerationError(actionErrorMessage(result))
                return
            }
            closeModeration()
        })
    }

    const handleUnblock = blockedPlayerId => {
        dispatch(unblockUser(blockedPlayerId, 'Cinderella', 'RecapComments'))
        .then(result => {
            if (result.error || result.payload?.error || result.payload?.response?.error) {
                dispatch(createNotification({ headline: 'Unblock User', message: actionErrorMessage(result), type: 'error', timeout: 5000 }))
                return
            }
            dispatch(getRecapComments(recapId))
        })
    }

    const handleUndoReport = reportedItemId => {
        dispatch(withdrawReportedItem(reportedItemId))
        .then(result => {
            if (result.error || result.payload?.error || result.payload?.response?.error) {
                dispatch(getMyReportedItems('Cinderella', 'RecapComment'))
                dispatch(createNotification({
                    headline: 'Undo Report',
                    message: actionErrorMessage(result, 'The report could not be withdrawn.'),
                    type: 'error',
                    timeout: 5000,
                }))
            }
        })
    }

    const hiddenPlayerIds = new Set([
        ...blockedPlayerIds,
        ...(recapComments.blockedPlayerIds || []),
    ].map(i => `${i}`))
    const moderationComment = comments.find(i => i._id === moderationCommentId)

    if (!recapId) return null
    return (
        <div className={s.container}>
            <ListGroup className={s.commentList}>
            {comments && comments.map(i => {
                const myReportedItem = myReportedItems.find(report => `${report.itemId}` === `${i._id}`) || null
                const adminReportedItem = isCinderellaAdmin ? (reportedItems.find(report => `${report.itemId}` === `${i._id}`) || null) : null
                return (
                <RecapComment {...i} key={`recap-comment-${i._id}`}
                    currentTime={currentTime}
                    canDelete={isCinderellaAdmin || (playerId === i.playerId)}
                    canEdit={playerId === i.playerId}
                    canModerate={`${playerId}` !== `${i.playerId}`}
                    onEdit={handleEdit}
                    isEditing={editCommentId === i._id}
                    isBlocked={i.isBlocked || hiddenPlayerIds.has(`${i.playerId}`)}
                    reportedItem={myReportedItem || adminReportedItem}
                    canUndoReport={!!myReportedItem}
                    isSavingBlock={isSavingBlock}
                    isSavingReport={isSavingReport}
                    onDelete={handleDeleteComment}
                    onReport={commentId => openModeration('report', commentId)}
                    onBlock={commentId => openModeration('block', commentId)}
                    onUnblock={handleUnblock}
                    onUndoReport={handleUndoReport} />
                )
            })}
            {!comments || !comments.length ?
                <ListGroup.Item className={s.commentItem}>
                    <div className={s.comment + ' ' + s.noComments}>No comments for this recap located</div>
                </ListGroup.Item>
            : null}
            </ListGroup>
            <NewRecapComment newComment={newComment} 
                avatarUrl={avatarUrl} onPost={handleCommentPost}
                onCancelEdit={handleCancelEdit} editMode={!!editCommentId}
                inputRef={_commentInput}
                onChange={e => setNewComment(e.target.value)} />
            <div ref={_bottomOfComments} />
            <ModerationModal action={moderationAction} comment={moderationComment}
                error={moderationError} isSaving={isSavingBlock || isSavingReport}
                onClose={closeModeration} onSubmit={handleModeration} />
        </div>
    )
}

export default RecapComments
